import { FONT_B64 } from "./fontData";

// CGA palette as the original game configures it:
// Palette(0) -> entries 1..3 = green, red, brown; GraphBackground(1) -> index 0 = blue
export const PALETTE = [
  [0, 0, 170],
  [0, 170, 0],
  [170, 0, 0],
  [170, 85, 0],
];

export const GRID_COLS = 40;
export const GRID_ROWS = 25;

// Decode the original FONTHE8.COM 8x8 glyph table (256 glyphs x 8 bytes)
export function decodeFont() {
  const bin = atob(FONT_B64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

const MIRROR = { "(": ")", ")": "(", "<": ">", ">": "<", "[": "]", "]": "[" };

// Convert a logical Hebrew string to visual order (the way the original
// game stores and renders its strings): reverse the string while keeping
// digit/Latin runs intact and mirroring paired punctuation.
export function vis(s) {
  const tokens = s.match(/[0-9A-Za-z]+(?:[,.:%][0-9A-Za-z]+)*%?|[\s\S]/g) || [];
  return tokens
    .reverse()
    .map((t) => (t.length > 1 || /[0-9A-Za-z]/.test(t) ? t : MIRROR[t] || t))
    .join("");
}

// Map a JS character to a CP862 glyph index
const SPECIAL = { "─": 0xc4, "┘": 0xd9, "◄": 17, "·": 250, "█": 219, "▒": 177 };

export function charToCp862(ch) {
  const cp = ch.codePointAt(0);
  if (cp >= 0x05d0 && cp <= 0x05ea) return 0x80 + (cp - 0x05d0);
  if (cp < 128) return cp;
  if (SPECIAL[ch] !== undefined) return SPECIAL[ch];
  return 0x3f; // '?'
}

export class Screen {
  constructor(canvas, assets, fontBytes) {
    this.canvas = canvas;
    this.ctx = canvas.getContext("2d", { willReadFrequently: true });
    this.assets = assets;
    this.font = fontBytes;
    this.clear(0);
  }

  clear(colorIndex) {
    const [r, g, b] = PALETTE[colorIndex & 3];
    this.ctx.fillStyle = `rgb(${r},${g},${b})`;
    this.ctx.fillRect(0, 0, 320, 200);
  }

  // Blue DOS background (GraphBackground(1)) - same as palette index 0
  clearBlue() {
    this.clear(0);
  }

  // PutPic semantics: place picture with its BOTTOM-left corner at (x, bottomY)
  putPic(name, x, bottomY) {
    const img = this.assets[name];
    if (!img) return;
    this.ctx.drawImage(img, x, bottomY - img.height + 1);
  }

  putData(data, x, topY) {
    this.ctx.putImageData(data, x, topY);
  }

  getData(x1, y1, x2, y2) {
    return this.ctx.getImageData(x1, y1, x2 - x1 + 1, y2 - y1 + 1);
  }

  snapshot() {
    return this.ctx.getImageData(0, 0, 320, 200);
  }

  restore(snap) {
    this.ctx.putImageData(snap, 0, 0);
  }

  // Copy a region of the screen into a reusable offscreen canvas (GPU blit,
  // avoids per-frame ImageData allocations that strain mobile browsers)
  copyRegion(x, y, w, h) {
    const c = document.createElement("canvas");
    c.width = w;
    c.height = h;
    c.getContext("2d").drawImage(this.canvas, x, y, w, h, 0, 0, w, h);
    return c;
  }

  drawCanvas(c, x, y) {
    this.ctx.drawImage(c, x, y);
  }

  // ColorTable(3,2,1,0) + PutPic of the same area = palette inversion
  invertRect(x1, y1, x2, y2) {
    const d = this.ctx.getImageData(x1, y1, x2 - x1 + 1, y2 - y1 + 1);
    const p = d.data;
    // identify palette index by channel bits (>84 handles brown's g=85),
    // then map 0<->3, 1<->2
    const map = { 1: 3, 2: 2, 4: 1, 6: 0 }; // blue->brown, green->red, red->green, brown->blue
    for (let i = 0; i < p.length; i += 4) {
      const key = ((p[i] > 84 ? 1 : 0) << 2) | ((p[i + 1] > 84 ? 1 : 0) << 1) | (p[i + 2] > 84 ? 1 : 0);
      const idx = map[key] !== undefined ? map[key] : 3;
      p[i] = PALETTE[idx][0];
      p[i + 1] = PALETTE[idx][1];
      p[i + 2] = PALETTE[idx][2];
    }
    this.ctx.putImageData(d, x1, y1);
  }

  drawGlyphPx(px, py, code, colorIndex, bgIndex = 0) {
    const d = this.ctx.getImageData(px, py, 8, 8);
    const p = d.data;
    const fg = PALETTE[colorIndex & 3];
    const bg = PALETTE[bgIndex & 3];
    for (let row = 0; row < 8; row++) {
      const bits = this.font[code * 8 + row];
      for (let bit = 0; bit < 8; bit++) {
        const on = (bits & (0x80 >> bit)) !== 0;
        const o = (row * 8 + bit) * 4;
        const c = on ? fg : bg;
        p[o] = c[0];
        p[o + 1] = c[1];
        p[o + 2] = c[2];
        p[o + 3] = 255;
      }
    }
    this.ctx.putImageData(d, px, py);
  }

  // Write VISUAL-order text at 1-based grid cell. Columns past 40 wrap
  // within the same row (CGA BIOS quirk the original game relies on).
  writeAt(col, row, text, colorIndex = 3) {
    let c = ((col - 1) % GRID_COLS) + 1;
    let r = row;
    for (const ch of text) {
      this.drawGlyphPx((c - 1) * 8, (r - 1) * 8, charToCp862(ch), colorIndex);
      c += 1;
      if (c > GRID_COLS) {
        c = 1;
        r += 1;
        if (r > GRID_ROWS) r = GRID_ROWS;
      }
    }
  }

  // Pixel-positioned text (PortTextAt) for overlays between grid rows
  writePx(x, y, text, colorIndex = 3) {
    let px = x;
    for (const ch of text) {
      this.drawGlyphPx(px, y, charToCp862(ch), colorIndex);
      px += 8;
    }
  }
}
